import { Scene } from 'phaser'
import { useGameStore } from '../../store/gameStore'
import { useMetaStore } from '../../meta/metaStore'
import {
  cameraNeedsPan,
  clampScroll,
  focusAroundLogical,
  POINTER_DRAG_THRESHOLD,
  preserveViewportFocus,
  resolveViewportPolicy,
  screenFromLogical,
  screenToGrid,
  worldFromLogical,
  type GridView
} from '../model/coords'
import { ENEMY_CATALOG } from '../model/enemies'
import {
  effectiveSlow,
  effectiveSpeed,
  statusRemaining
} from '../model/effects'
import { getMap } from '../model/mapCatalog'
import { DEFAULT_MAP_ID } from '../model/mapCatalog'
import { GameSession } from '../model/session'
import { attachSession, detachSession } from '../model/sessionHost'
import { createCampaignSession, getChapter } from '../campaign'
import { mapPrimaryFocus, resolveAirRoute } from '../model/path'
import {
  FIRE_TOWER,
  footprintCenter,
  footprintCells,
  TOWER_CATALOG,
  type TowerDefinition,
  type TowerInstance
} from '../model/towers'
import type { GridPosition, MapDefinition } from '../model/types'
import { validateMap } from '../model/validateMap'
import { getAudioManager } from '../audio'
import {
  RENDER_LAYERS,
  THEME,
  alignPx,
  activeRouteIdsFromMapWaves,
  paintBoardTerrain,
  paintEnemyVisual,
  paintProjectileVisual,
  paintTowerVisual,
  resolveEnemyVisual,
  resolveProjectileVisual,
  resolveTowerVisual,
  strokeWidthForCell,
  PresentationClock,
  prefersReducedMotion,
  scaleSimDurationMs,
  worldTweenTimeScale,
  towerIdleMotion,
  towerIdlePhase,
  sampleTowerIdle,
  towerFireKickScale,
  towerFireKickSimMs,
  towerUpgradeKickScale,
  towerSellKickScale,
  enemyIdleMotion,
  enemyIdlePhase,
  sampleEnemyIdle,
  facingFromDelta,
  approachAngle,
  enemyFamilyId,
  isAirEnemyFamily,
  bossRingSpin,
  enemySpawnStartScale,
  enemySpawnSimMs,
  enemyDeathBurstScale,
  enemyDeathSimMs,
  admitCombatFx,
  mergeGoldPopup,
  damageFloaterLook,
  formatCoinsFloater,
  FX_BUDGET,
  type MotionPrefs,
  type FxLiveCounts,
  type GoldAggregate
} from '../visual'
import { fillPolygon, regularPolygon, strokePolygon } from '../visual/drawShapes'

interface EnemyView {
  root: Phaser.GameObjects.Container
  silhouette: Phaser.GameObjects.Container
  body: Phaser.GameObjects.Graphics
  shadow: Phaser.GameObjects.Ellipse
  eliteRing: Phaser.GameObjects.Arc
  barBack: Phaser.GameObjects.Rectangle
  barFill: Phaser.GameObjects.Rectangle
  shieldBack: Phaser.GameObjects.Rectangle
  shieldFill: Phaser.GameObjects.Rectangle
  frostMark: Phaser.GameObjects.Arc
  poisonMark: Phaser.GameObjects.Arc
  burnMark: Phaser.GameObjects.Arc
  armorBreakMark: Phaser.GameObjects.Arc
  deepFreezeRing: Phaser.GameObjects.Arc
  bossRing: Phaser.GameObjects.Arc
  debug: Phaser.GameObjects.Text
  definitionId: string
  lastRadius: number
  lastShield: boolean
  lastElite: boolean
  lastRegen: boolean
  prevX: number
  prevY: number
  facing: number
  idlePhase: number
  spawnDone: boolean
}

interface TowerView {
  root: Phaser.GameObjects.Container
  silhouette: Phaser.GameObjects.Container
  art: Phaser.GameObjects.Graphics
  label: Phaser.GameObjects.Text
  range: Phaser.GameObjects.Arc
  definitionId: string
  level: number
  lastCell: number
  selected: boolean
  idlePhase: number
}

interface ProjectileView {
  art: Phaser.GameObjects.Graphics
  definitionId: string
  lastCell: number
  lastX: number
  lastY: number
}

interface PointerGesture {
  startX: number
  startY: number
  lastX: number
  lastY: number
  isPan: boolean
  pointerId: number
}

export class PlayingScene extends Scene {
  private worldRoot!: Phaser.GameObjects.Container
  private mapLayer!: Phaser.GameObjects.Container
  private markerLayer!: Phaser.GameObjects.Container
  private coordLayer!: Phaser.GameObjects.Container
  private pathLayer!: Phaser.GameObjects.Container
  private towerLayer!: Phaser.GameObjects.Container
  private previewLayer!: Phaser.GameObjects.Container
  private enemyLayer!: Phaser.GameObjects.Container
  private projectileLayer!: Phaser.GameObjects.Container
  private fxLayer!: Phaser.GameObjects.Container
  private uiLayer!: Phaser.GameObjects.Container
  private showCoordinates = false
  private showPath = false
  private definition: MapDefinition | null = null
  private session: GameSession | null = null
  private view: GridView | null = null
  private enemyViews = new Map<string, EnemyView>()
  private towerViews = new Map<string, TowerView>()
  private projectileViews = new Map<string, ProjectileView>()
  private previewGraphics: Phaser.GameObjects.Graphics | null = null
  private previewRange: Phaser.GameObjects.Arc | null = null
  private terrainGfx: Phaser.GameObjects.Graphics | null = null
  private gridGfx: Phaser.GameObjects.Graphics | null = null
  private boardEdgeGfx: Phaser.GameObjects.Graphics | null = null
  private markerGfx: Phaser.GameObjects.Graphics | null = null
  private backdropGfx: Phaser.GameObjects.Graphics | null = null
  private previewAnchor: GridPosition | null = null
  private touchConfirmAnchor: GridPosition | null = null
  private lastPlacementId: string | null = null
  private lastKnownPlacementId: string | null = null
  private lastKnownSelectedId: string | null = null
  private lastPaintedSessionState: string | null = null
  private unsubscribeStore: (() => void) | null = null
  private gesture: PointerGesture | null = null
  private cameraInitialized = false
  private presentationClock = new PresentationClock()
  private wasPaused = false
  private fxLive: FxLiveCounts = {
    damageNumbers: 0,
    goldPopups: 0,
    hitFlashes: 0,
    particles: 0
  }
  private pendingGold: GoldAggregate[] = []
  private goldFlushAt = 0

  constructor() {
    super({ key: 'PlayingScene' })
  }

  create(data?: { mapId?: string }): void {
    const store = useGameStore.getState()
    const mapId = data?.mapId ?? store.selectedMapId ?? DEFAULT_MAP_ID
    let definition = store.playtestMap
      ? structuredClone(store.playtestMap)
      : getMap(mapId)

    if (!store.playtestMap && store.selectedStageId) {
      try {
        const created = createCampaignSession({
          stageId: store.selectedStageId,
          difficultyId: store.selectedDifficultyId,
          difficultyMode: 'provisional',
          researchSnapshot: useMetaStore.getState().research
        })
        definition = created.map
      } catch {
        // fall through to catalog map / error path
      }
    }

    this.worldRoot = this.add.container(0, 0)
    this.mapLayer = this.add.container(0, 0)
    this.markerLayer = this.add.container(0, 0)
    this.coordLayer = this.add.container(0, 0)
    this.pathLayer = this.add.container(0, 0)
    this.towerLayer = this.add.container(0, 0)
    this.previewLayer = this.add.container(0, 0)
    this.enemyLayer = this.add.container(0, 0)
    this.projectileLayer = this.add.container(0, 0)
    this.fxLayer = this.add.container(0, 0)
    this.uiLayer = this.add.container(0, 0)

    this.mapLayer.setDepth(RENDER_LAYERS.terrain)
    this.pathLayer.setDepth(RENDER_LAYERS.pathDecoration)
    this.markerLayer.setDepth(RENDER_LAYERS.markers)
    this.coordLayer.setDepth(RENDER_LAYERS.debugOverlay)
    this.towerLayer.setDepth(RENDER_LAYERS.towers)
    this.previewLayer.setDepth(RENDER_LAYERS.placementPreview)
    this.enemyLayer.setDepth(RENDER_LAYERS.enemies)
    this.projectileLayer.setDepth(RENDER_LAYERS.projectiles)
    this.fxLayer.setDepth(RENDER_LAYERS.combatFx)
    this.uiLayer.setDepth(RENDER_LAYERS.uiChrome)

    this.worldRoot.add([
      this.mapLayer,
      this.pathLayer,
      this.markerLayer,
      this.coordLayer,
      this.towerLayer,
      this.previewLayer,
      this.enemyLayer,
      this.projectileLayer,
      this.fxLayer
    ])
    this.coordLayer.setVisible(false)
    this.pathLayer.setVisible(false)

    this.events.once('shutdown', () => {
      this.tweens.killAll()
      this.clearEnemyViews()
      this.clearTowerViews()
      this.clearProjectileViews()
      this.clearPreview()
      this.fxLayer?.removeAll(true)
      this.presentationClock.reset()
      this.fxLive = { damageNumbers: 0, goldPopups: 0, hitFlashes: 0, particles: 0 }
      this.pendingGold = []
      getAudioManager().resetMatchBookkeeping()
      this.unsubscribeStore?.()
      this.unsubscribeStore = null
      useGameStore.getState().cancelPlacement()
      useGameStore.getState().selectTowerId(null)
      detachSession()
      this.session = null
      this.scale.off('resize', this.handleResize, this)
    })

    if (!definition) {
      this.showMessage(`Unknown map: ${mapId}`)
      return
    }

    const validation = validateMap(definition)
    if (!validation.ok) {
      this.showMessage(validation.errors.map((error) => error.message).join('\n'))
      return
    }

    this.definition = definition
    this.input.mouse?.disableContextMenu()
    this.bindDebugKeys()
    this.bindPointer()
    this.beginSession(definition)
    this.rebuildBoard()
    getAudioManager().unlock()
    this.input.on('pointerdown', () => getAudioManager().unlock())
    this.scale.on('resize', this.handleResize, this)
    this.unsubscribeStore = useGameStore.subscribe((state) => {
      const selected = state.placementDefinitionId
      const selectedTowerId = state.selectedTowerId
      if (selected !== this.lastKnownPlacementId) {
        this.lastKnownPlacementId = selected
        this.touchConfirmAnchor = null
        if (!selected) {
          this.clearPreview()
          this.previewAnchor = null
        }
        this.refreshTerrainGrid()
        this.redrawPreview()
      }
      if (selectedTowerId !== this.lastKnownSelectedId) {
        this.lastKnownSelectedId = selectedTowerId
        this.syncTowers()
      }
    })
    this.lastKnownPlacementId = useGameStore.getState().placementDefinitionId
    this.lastKnownSelectedId = useGameStore.getState().selectedTowerId
  }

  update(_time: number, delta: number): void {
    if (!this.session) return
    const prefs = this.motionPrefs()
    const pausedNow = prefs.paused
    if (pausedNow !== this.wasPaused) {
      this.wasPaused = pausedNow
      if (pausedNow) this.tweens.pauseAll()
      else this.tweens.resumeAll()
    }
    this.tweens.timeScale = worldTweenTimeScale(prefs)
    this.presentationClock.tick(delta / 1000, prefs)

    this.session.update(delta / 1000)
    if (this.session.state !== this.lastPaintedSessionState) {
      this.lastPaintedSessionState = this.session.state
      this.refreshTerrainGrid()
    }
    this.syncEnemies()
    this.syncTowers()
    this.syncProjectiles()
    this.flushPendingGold()
    this.syncCombatFx()
    const audio = getAudioManager()
    audio.setSimulationSpeed(prefs.simulationSpeed)
    audio.syncMatchPresentation({
      state: this.session.state,
      enemies: this.session.enemies.map((e) => ({
        id: e.id,
        definitionId: e.definitionId
      }))
    })
    useGameStore.getState().publishSessionView(this.session.view())
  }

  private motionPrefs(): MotionPrefs {
    return {
      reducedMotion: prefersReducedMotion(),
      paused: this.session?.paused === true,
      simulationSpeed: this.session?.simulationSpeed ?? 1
    }
  }

  restartSession(): void {
    if (!this.definition) return
    this.tweens.killAll()
    this.clearEnemyViews()
    this.clearTowerViews()
    this.clearProjectileViews()
    this.clearPreview()
    this.fxLayer.removeAll(true)
    this.presentationClock.reset()
    this.wasPaused = false
    this.fxLive = { damageNumbers: 0, goldPopups: 0, hitFlashes: 0, particles: 0 }
    this.pendingGold = []
    this.goldFlushAt = 0
    this.tweens.timeScale = 1
    getAudioManager().resetMatchBookkeeping()
    this.previewAnchor = null
    this.touchConfirmAnchor = null
    this.cameraInitialized = false
    useGameStore.getState().cancelPlacement()
    useGameStore.getState().selectTowerId(null)
    this.beginSession(this.definition)
    this.rebuildBoard()
  }

  private beginSession(definition: MapDefinition): void {
    const store = useGameStore.getState()
    // Always clone so play/restart cannot mutate catalog or editor drafts.
    if (store.playtestMap) {
      this.session = new GameSession(structuredClone(definition))
    } else if (store.selectedStageId) {
      const created = createCampaignSession({
        stageId: store.selectedStageId,
        difficultyId: store.selectedDifficultyId,
        difficultyMode: 'provisional',
        researchSnapshot: useMetaStore.getState().research
      })
      this.definition = created.map
      this.session = created.session
    } else {
      this.session = new GameSession(structuredClone(definition))
    }
    attachSession(this.session, () => this.restartSession())
    useGameStore.getState().publishSessionView(this.session.view())
  }

  private handleResize = (): void => {
    this.rebuildBoard()
  }

  private rebuildBoard(): void {
    if (!this.definition || !this.session) return

    this.mapLayer.removeAll(true)
    this.markerLayer.removeAll(true)
    this.coordLayer.removeAll(true)
    this.pathLayer.removeAll(true)
    this.uiLayer.removeAll(true)
    this.terrainGfx = null
    this.gridGfx = null
    this.boardEdgeGfx = null
    this.markerGfx = null
    this.clearTowerViews()
    this.clearPreview()
    this.paintViewportBackdrop()

    const { width, height } = this.scale
    const previous = this.view
    const decision = resolveViewportPolicy(
      this.definition.width,
      this.definition.height,
      width,
      height,
      { padding: 2 }
    )
    let next = decision.view
    if (!this.cameraInitialized) {
      if (decision.mode === 'FOCUS' || cameraNeedsPan(next)) {
        next = focusAroundLogical(next, mapPrimaryFocus(this.definition))
      }
      this.cameraInitialized = true
    } else if (previous) {
      const modeChanged = previous.cameraMode !== next.cameraMode
      const panBecameNeeded =
        (!cameraNeedsPan(previous) && cameraNeedsPan(next)) ||
        (previous.maxScrollY === 0 && next.maxScrollY > 0) ||
        (previous.maxScrollX === 0 && next.maxScrollX > 0)
      if (modeChanged || panBecameNeeded) {
        if (decision.mode === 'FOCUS' || cameraNeedsPan(next)) {
          next = focusAroundLogical(next, mapPrimaryFocus(this.definition))
        }
      } else {
        next = preserveViewportFocus(previous, next)
        if (cameraNeedsPan(next)) {
          const spawn = mapPrimaryFocus(this.definition)
          const screen = screenFromLogical(next, spawn)
          if (
            screen.y < next.cellSize * 2 ||
            screen.y > next.viewportHeight - next.cellSize ||
            screen.x < 0 ||
            screen.x > next.viewportWidth
          ) {
            next = focusAroundLogical(next, spawn)
          }
        }
      }
    }
    this.view = next
    this.applyCamera()

    this.renderMap(this.session, this.view)
    this.renderMarkers(this.definition, this.view)
    this.renderCoordinates(this.definition, this.view)
    this.renderPath(this.definition, this.view)
    this.createHudChrome(this.definition, height)
    this.coordLayer.setVisible(this.showCoordinates)
    this.pathLayer.setVisible(this.showPath)
    this.syncEnemies()
    this.syncTowers()
    this.syncProjectiles()
    this.redrawPreview()
  }

  private applyCamera(): void {
    if (!this.view) return
    this.worldRoot.setPosition(
      this.view.offsetX - this.view.scrollX,
      this.view.offsetY - this.view.scrollY
    )
  }

  private panBy(dx: number, dy: number): void {
    if (!this.view) return
    if (this.view.maxScrollX === 0 && this.view.maxScrollY === 0) return
    const next = clampScroll(this.view, this.view.scrollX - dx, this.view.scrollY - dy)
    this.view = { ...this.view, scrollX: next.scrollX, scrollY: next.scrollY }
    this.applyCamera()
  }

  private showMessage(text: string): void {
    const { width, height } = this.scale
    this.add.text(width / 2, height / 2, text, {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#fecaca',
      align: 'center',
      wordWrap: { width: width - 40 }
    }).setOrigin(0.5)
  }

  private renderMap(session: GameSession, view: GridView): void {
    if (!this.definition) return

    this.boardEdgeGfx = this.add.graphics()
    this.terrainGfx = this.add.graphics()
    this.gridGfx = this.add.graphics()
    this.markerGfx = this.add.graphics()
    this.mapLayer.add([this.boardEdgeGfx, this.terrainGfx, this.gridGfx, this.markerGfx])

    paintBoardTerrain(
      this.terrainGfx,
      this.gridGfx,
      this.boardEdgeGfx,
      this.markerGfx,
      session,
      this.definition,
      view,
      {
        placing: this.isPlacing(),
        activeRouteIds: activeRouteIdsFromMapWaves(this.definition.waves),
        sessionState: session.state,
        biomeId: this.resolveBiomeId(session)
      }
    )
    this.lastPaintedSessionState = session.state
  }

  private resolveBiomeId(session: GameSession): string | undefined {
    const chapterId = session.campaignContext?.chapterId
    if (!chapterId) return undefined
    return getChapter(chapterId)?.biomeId
  }

  private refreshTerrainGrid(): void {
    if (!this.session || !this.view || !this.definition) return
    if (!this.terrainGfx || !this.gridGfx || !this.boardEdgeGfx || !this.markerGfx) return
    paintBoardTerrain(
      this.terrainGfx,
      this.gridGfx,
      this.boardEdgeGfx,
      this.markerGfx,
      this.session,
      this.definition,
      this.view,
      {
        placing: this.isPlacing(),
        activeRouteIds: activeRouteIdsFromMapWaves(this.definition.waves),
        sessionState: this.session.state,
        biomeId: this.resolveBiomeId(this.session)
      }
    )
  }

  private renderMarkers(_definition: MapDefinition, _view: GridView): void {
    // Spawn/exit markers are painted by paintBoardTerrain into markerGfx.
  }

  private addMarker(
    view: GridView,
    x: number,
    y: number,
    label: string,
    color: string
  ): void {
    const center = worldFromLogical(view, { x: x + 0.5, y: y + 0.5 })
    const fontSize = Math.max(10, Math.floor(view.cellSize * 0.55))
    const text = this.add.text(center.x, center.y, label, {
      fontSize: `${fontSize}px`,
      fontFamily: 'Arial, sans-serif',
      color,
      fontStyle: 'bold'
    }).setOrigin(0.5)
    this.markerLayer.add(text)
  }

  private renderCoordinates(definition: MapDefinition, view: GridView): void {
    const fontSize = Math.max(6, Math.floor(view.cellSize * 0.28))
    for (let y = 0; y < definition.height; y += 1) {
      for (let x = 0; x < definition.width; x += 1) {
        const center = worldFromLogical(view, { x: x + 0.5, y: y + 0.5 })
        const text = this.add.text(center.x, center.y, `${x},${y}`, {
          fontSize: `${fontSize}px`,
          fontFamily: 'Arial, sans-serif',
          color: '#ecfccb'
        }).setOrigin(0.5)
        this.coordLayer.add(text)
      }
    }
  }

  private renderPath(definition: MapDefinition, view: GridView): void {
    const graphics = this.add.graphics()
    graphics.lineStyle(Math.max(2, view.cellSize * 0.1), 0xfbbf24, 0.9)
    definition.path.forEach((cell, index) => {
      const center = worldFromLogical(view, { x: cell.x + 0.5, y: cell.y + 0.5 })
      if (index === 0) graphics.moveTo(center.x, center.y)
      else graphics.lineTo(center.x, center.y)
      graphics.fillStyle(0xfbbf24, 0.95)
      graphics.fillCircle(center.x, center.y, Math.max(2, view.cellSize * 0.1))
    })
    graphics.strokePath()

    // Editor/debug: stronger AIR polyline when path overlay is on
    const air = resolveAirRoute(definition)
    if (air && air.cells.length > 0) {
      graphics.lineStyle(Math.max(2, view.cellSize * 0.08), 0x38bdf8, 0.85)
      air.cells.forEach((cell, index) => {
        const center = worldFromLogical(view, { x: cell.x + 0.5, y: cell.y + 0.5 })
        if (index === 0) graphics.moveTo(center.x, center.y)
        else graphics.lineTo(center.x, center.y)
        graphics.fillStyle(0x7dd3fc, 0.95)
        graphics.fillCircle(center.x, center.y, Math.max(2, view.cellSize * 0.09))
      })
      graphics.strokePath()
    }
    this.pathLayer.add(graphics)
  }

  private createHudChrome(definition: MapDefinition, height: number): void {
    const compact = height < 420 || this.scale.width < 520
    const title = this.add.text(6, compact ? height - 16 : height - 28, definition.name, {
      fontSize: compact ? '10px' : '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#f8fafc',
      fontStyle: 'bold'
    })
    this.uiLayer.add(title)
    if (!compact) {
      const hint = this.add.text(6, height - 14, 'Drag pan  ·  G coords  ·  P path  ·  ESC', {
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        color: '#d9f99d'
      })
      this.uiLayer.add(hint)
    }
  }

  private bindPointer(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        if (this.isPlacing()) this.cancelPlacement()
        return
      }
      if (!pointer.leftButtonDown() && pointer.button !== 0) return

      this.gesture = {
        startX: pointer.x,
        startY: pointer.y,
        lastX: pointer.x,
        lastY: pointer.y,
        isPan: false,
        pointerId: pointer.id
      }
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.gesture && this.gesture.pointerId === pointer.id && pointer.isDown) {
        const dx = pointer.x - this.gesture.lastX
        const dy = pointer.y - this.gesture.lastY
        const total = Math.hypot(pointer.x - this.gesture.startX, pointer.y - this.gesture.startY)
        if (!this.gesture.isPan && total >= POINTER_DRAG_THRESHOLD) {
          this.gesture.isPan = true
        }
        if (this.gesture.isPan) {
          this.panBy(dx, dy)
          this.gesture.lastX = pointer.x
          this.gesture.lastY = pointer.y
          return
        }
      }

      if (this.isPlacing() && !pointer.wasTouch && !this.gesture?.isPan) {
        this.updatePreviewFromScreen(pointer.x, pointer.y)
      }
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const gesture = this.gesture
      this.gesture = null
      if (!gesture || gesture.pointerId !== pointer.id) return
      if (gesture.isPan) return
      this.handleTap(pointer.x, pointer.y, pointer.wasTouch)
    })

    this.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) => {
      if (this.gesture?.pointerId === pointer.id) this.gesture = null
    })
  }

  private handleTap(screenX: number, screenY: number, wasTouch: boolean): void {
    const cell = this.cellFromScreen(screenX, screenY)
    if (!cell) return

    if (this.isPlacing()) {
      if (wasTouch) {
        this.handleTouchPlacement(cell)
        return
      }
      this.tryPlace(cell)
      return
    }

    this.handleSelectionTap(cell)
  }

  private handleSelectionTap(cell: GridPosition): void {
    if (!this.session) return
    const towerId = this.session.towerIdAt(cell)
    useGameStore.getState().selectTowerId(towerId)
  }

  private handleTouchPlacement(cell: GridPosition): void {
    const same =
      this.touchConfirmAnchor &&
      this.touchConfirmAnchor.x === cell.x &&
      this.touchConfirmAnchor.y === cell.y

    this.updatePreviewFromCell(cell)

    if (same) {
      const placed = this.tryPlace(cell)
      if (placed) this.touchConfirmAnchor = null
      return
    }

    this.touchConfirmAnchor = { ...cell }
  }

  private tryPlace(anchor: GridPosition): boolean {
    if (!this.session) return false
    const definitionId = useGameStore.getState().placementDefinitionId
    if (!definitionId) return false

    const result = this.session.placeTower(definitionId, anchor)
    useGameStore.getState().publishSessionView(this.session.view())
    if (!result.ok) {
      this.redrawPreview()
      return false
    }

    getAudioManager().unlock()
    getAudioManager().play('ui.place')
    this.lastPlacementId = result.tower.id
    this.syncTowers()
    this.clearPreview()
    this.previewAnchor = null
    this.touchConfirmAnchor = null
    useGameStore.getState().cancelPlacement()
    useGameStore.getState().selectTowerId(result.tower.id)
    return true
  }

  private updatePreviewFromScreen(screenX: number, screenY: number): void {
    const cell = this.cellFromScreen(screenX, screenY)
    if (!cell) {
      this.clearPreview()
      this.previewAnchor = null
      return
    }
    this.updatePreviewFromCell(cell)
  }

  private updatePreviewFromCell(cell: GridPosition): void {
    this.previewAnchor = { ...cell }
    this.redrawPreview()
  }

  private cellFromScreen(screenX: number, screenY: number): GridPosition | null {
    if (!this.view || !this.definition) return null
    const cell = screenToGrid(this.view, screenX, screenY)
    if (
      cell.x < 0 ||
      cell.y < 0 ||
      cell.x >= this.definition.width ||
      cell.y >= this.definition.height
    ) {
      return null
    }
    return cell
  }

  private isPlacing(): boolean {
    return Boolean(useGameStore.getState().placementDefinitionId) && !useGameStore.getState().isPaused
  }

  private selectedDefinition(): TowerDefinition | null {
    const id = useGameStore.getState().placementDefinitionId
    if (!id || !this.session) return null
    return this.session.getTowerDefinition(id) ?? TOWER_CATALOG[id] ?? null
  }

  private redrawPreview(): void {
    this.clearPreview()
    if (!this.session || !this.view || !this.previewAnchor) return
    const definition = this.selectedDefinition()
    if (!definition) return

    const preview = this.session.previewPlacement(definition.id, this.previewAnchor)
    const cells = preview.ok
      ? preview.tower.occupiedCells
      : footprintCells(this.previewAnchor, definition.footprint)
    const valid = preview.ok
    const color = valid ? THEME.valid : THEME.invalid
    const graphics = this.add.graphics()
    const cell = this.view.cellSize
    const stroke = strokeWidthForCell(cell, 'bold')

    for (const occupied of cells) {
      const topLeft = worldFromLogical(this.view, { x: occupied.x, y: occupied.y })
      const px = alignPx(topLeft.x)
      const py = alignPx(topLeft.y)
      graphics.fillStyle(color, valid ? 0.28 : 0.22)
      graphics.fillRect(px, py, cell, cell)
      graphics.lineStyle(stroke, color, 1)
      graphics.strokeRect(px + 1, py + 1, cell - 2, cell - 2)
      if (!valid) {
        // Danger pattern — not color-only
        graphics.lineStyle(strokeWidthForCell(cell, 'thin'), THEME.danger, 0.95)
        graphics.lineBetween(px + 3, py + 3, px + cell - 3, py + cell - 3)
        graphics.lineBetween(px + cell - 3, py + 3, px + 3, py + cell - 3)
      } else {
        // Corner marks for footprint readability
        const t = Math.max(3, cell * 0.2)
        graphics.lineStyle(stroke, THEME.white, 0.85)
        graphics.lineBetween(px + 2, py + 2, px + 2 + t, py + 2)
        graphics.lineBetween(px + 2, py + 2, px + 2, py + 2 + t)
        graphics.lineBetween(px + cell - 2, py + 2, px + cell - 2 - t, py + 2)
        graphics.lineBetween(px + cell - 2, py + 2, px + cell - 2, py + 2 + t)
      }
    }

    const center = footprintCenter(this.previewAnchor, definition.footprint)
    const world = worldFromLogical(this.view, center)
    const rangeCells = this.session?.combatRange(definition.id, 1) ?? definition.levels[0]!.range
    const range = rangeCells * this.view.cellSize
    const rangeStroke = Math.max(2, strokeWidthForCell(cell, 'medium'))
    // Dark understroke so the ring stays readable on grass + path
    const rangeUnder = this.add.circle(world.x, world.y, range)
    rangeUnder.setStrokeStyle(rangeStroke + 2, 0x0b1220, 0.55)
    rangeUnder.setFillStyle(color, 0.06)
    const rangeCircle = this.add.circle(world.x, world.y, range)
    rangeCircle.setStrokeStyle(rangeStroke, color, 0.78)
    rangeCircle.setFillStyle(color, 0.05)

    this.previewLayer.add(graphics)
    this.previewLayer.add(rangeUnder)
    this.previewLayer.add(rangeCircle)
    this.previewGraphics = graphics
    this.previewRange = rangeCircle
  }

  private clearPreview(): void {
    this.previewGraphics?.destroy()
    this.previewRange?.destroy()
    this.previewGraphics = null
    this.previewRange = null
    this.previewLayer.removeAll(true)
  }

  private syncTowers(): void {
    if (!this.session || !this.view) return
    const view = this.view
    const alive = new Set(this.session.towers.map((tower) => tower.id))

    for (const [id, towerView] of this.towerViews) {
      if (alive.has(id)) continue
      this.tweens.killTweensOf(towerView.silhouette)
      this.tweens.killTweensOf(towerView.root)
      towerView.root.destroy(true)
      towerView.range.destroy()
      this.towerViews.delete(id)
    }

    for (const tower of this.session.towers) {
      let towerView = this.towerViews.get(tower.id)
      if (!towerView) {
        towerView = this.createTowerView(tower, view)
        this.towerViews.set(tower.id, towerView)
      }
      this.layoutTowerView(tower, towerView, view)
    }
  }

  private createTowerView(tower: TowerInstance, view: GridView): TowerView {
    const visual = resolveTowerVisual(tower.definitionId)
    const art = this.add.graphics()
    // Letter labels removed from world entities (M32) — silhouette is the identity.
    const label = this.add.text(0, 0, '', {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: visual.labelColor,
      fontStyle: 'bold'
    }).setOrigin(0.5).setVisible(false)
    const silhouette = this.add.container(0, 0, [art])
    const root = this.add.container(0, 0, [silhouette, label])
    const range = this.add.circle(0, 0, 10)
    range.setStrokeStyle(1, visual.fill, 0.25)
    range.setFillStyle(visual.fill, 0.04)
    range.setVisible(false)
    range.setDepth(RENDER_LAYERS.towerRange)
    this.towerLayer.add([range, root])
    return {
      root,
      silhouette,
      art,
      label,
      range,
      definitionId: tower.definitionId,
      level: -1,
      lastCell: -1,
      selected: false,
      idlePhase: towerIdlePhase(tower.id)
    }
  }

  /** Full-viewport gutter wash (not under worldRoot) so desktop gutters read intentional. */
  private paintViewportBackdrop(): void {
    const { width, height } = this.scale
    if (!this.backdropGfx) {
      this.backdropGfx = this.add.graphics()
      this.backdropGfx.setDepth(RENDER_LAYERS.backdrop)
      this.backdropGfx.setScrollFactor(0)
    }
    this.backdropGfx.clear()
    this.backdropGfx.fillStyle(THEME.background, 1)
    this.backdropGfx.fillRect(0, 0, width, height)
  }

  private layoutTowerView(tower: TowerInstance, towerView: TowerView, view: GridView): void {
    const definition = TOWER_CATALOG[tower.definitionId] ?? this.session?.getTowerDefinition(tower.definitionId)
    const visual = resolveTowerVisual(tower.definitionId)
    const footprint = definition?.footprint ?? { width: 2, height: 2 }
    const width = footprint.width * view.cellSize
    const height = footprint.height * view.cellSize
    const center = worldFromLogical(view, footprintCenter(tower.anchor, footprint))
    const selectedId = useGameStore.getState().selectedTowerId
    const selected = tower.id === selectedId

    if (
      towerView.level !== tower.level ||
      towerView.lastCell !== view.cellSize ||
      towerView.definitionId !== tower.definitionId ||
      towerView.selected !== selected
    ) {
      paintTowerVisual(towerView.art, visual, tower.level, width, height, view.cellSize, {
        selected
      })
      towerView.level = tower.level
      towerView.lastCell = view.cellSize
      towerView.definitionId = tower.definitionId
      towerView.selected = selected
      towerView.label.setVisible(false)
    }

    towerView.root.setPosition(alignPx(center.x), alignPx(center.y))
    towerView.label.setVisible(false)

    const idle = sampleTowerIdle(
      towerIdleMotion(tower.definitionId, tower.level, this.motionPrefs()),
      this.presentationClock.time,
      towerView.idlePhase
    )
    // Fire/upgrade tweens may temporarily scale root — idle lives on silhouette.
    towerView.silhouette.setScale(idle.scale)
    towerView.silhouette.setRotation(idle.rotation)

    const rangeCells =
      this.session?.combatRange(tower.definitionId, tower.level) ??
      (definition ?? FIRE_TOWER).levels[tower.level - 1]?.range ??
      0
    towerView.range.setPosition(center.x, center.y)
    towerView.range.setRadius(rangeCells * view.cellSize)
    towerView.range.setVisible(selected)
    towerView.range.setStrokeStyle(
      selected ? strokeWidthForCell(view.cellSize, 'medium') : strokeWidthForCell(view.cellSize, 'thin'),
      selected ? THEME.selection : visual.fill,
      selected ? 0.85 : 0.35
    )
    towerView.range.setFillStyle(visual.fill, selected ? 0.08 : 0.03)
  }

  private clearTowerViews(): void {
    for (const towerView of this.towerViews.values()) {
      this.tweens.killTweensOf(towerView.silhouette)
      this.tweens.killTweensOf(towerView.root)
      towerView.root.destroy(true)
      towerView.range.destroy()
    }
    this.towerViews.clear()
  }

  private syncProjectiles(): void {
    if (!this.session || !this.view) return
    const view = this.view
    const alive = new Set(this.session.projectiles.map((projectile) => projectile.id))

    for (const [id, projectileView] of this.projectileViews) {
      if (alive.has(id)) continue
      projectileView.art.destroy()
      this.projectileViews.delete(id)
    }

    for (const projectile of this.session.projectiles) {
      let projectileView = this.projectileViews.get(projectile.id)
      const visual = resolveProjectileVisual(projectile.definitionId)
      const factor =
        projectile.splashRadius > 0
          ? Math.max(visual.radiusFactor, 0.18)
          : visual.radiusFactor
      const radius = Math.max(2, view.cellSize * factor)
      if (!projectileView) {
        const art = this.add.graphics()
        this.projectileLayer.add(art)
        projectileView = {
          art,
          definitionId: projectile.definitionId,
          lastCell: -1,
          lastX: projectile.x,
          lastY: projectile.y
        }
        this.projectileViews.set(projectile.id, projectileView)
      }
      if (
        projectileView.lastCell !== view.cellSize ||
        projectileView.definitionId !== projectile.definitionId
      ) {
        paintProjectileVisual(projectileView.art, visual, radius, view.cellSize)
        projectileView.definitionId = projectile.definitionId
        projectileView.lastCell = view.cellSize
      }
      const world = worldFromLogical(view, { x: projectile.x, y: projectile.y })
      // Short energy streak (presentation) — Fire/Poison especially.
      const prefs = this.motionPrefs()
      const moved =
        Math.hypot(projectile.x - projectileView.lastX, projectile.y - projectileView.lastY) > 0.02
      if (
        moved &&
        !prefs.reducedMotion &&
        this.fxLive.particles <
          (prefs.simulationSpeed >= 3 ? FX_BUDGET.maxParticlesAt3x : FX_BUDGET.maxParticles) &&
        (projectile.definitionId === 'fire' ||
          projectile.definitionId === 'poison' ||
          projectile.definitionId === 'mage')
      ) {
        const prev = worldFromLogical(view, {
          x: projectileView.lastX,
          y: projectileView.lastY
        })
        const streak = this.add.circle(
          (prev.x + world.x) / 2,
          (prev.y + world.y) / 2,
          Math.max(1.5, radius * 0.55),
          visual.fill,
          0.45
        )
        this.fxLayer.add(streak)
        this.fxLive.particles += 1
        this.tweens.add({
          targets: streak,
          alpha: 0,
          scale: 0.4,
          duration: scaleSimDurationMs({ simMs: 90, prefs }),
          onComplete: () => {
            streak.destroy()
            this.fxLive.particles = Math.max(0, this.fxLive.particles - 1)
          }
        })
      }
      projectileView.lastX = projectile.x
      projectileView.lastY = projectile.y
      projectileView.art.setPosition(world.x, world.y)
      projectileView.art.setScale(1)
    }
  }

  private clearProjectileViews(): void {
    for (const projectileView of this.projectileViews.values()) {
      projectileView.art.destroy()
    }
    this.projectileViews.clear()
  }

  private flushPendingGold(): void {
    if (!this.view || this.pendingGold.length === 0) return
    const now = this.presentationClock.time
    if (now < this.goldFlushAt && this.pendingGold.length < 4) return
    const prefs = this.motionPrefs()
    const batch = this.pendingGold
    this.pendingGold = []
    this.goldFlushAt = now + 0.12
    for (const g of batch) {
      if (!admitCombatFx({ type: 'goldPopup', x: g.x, y: g.y, amount: g.amount }, prefs, this.fxLive)) {
        continue
      }
      this.spawnGoldLabel(g.x, g.y, g.amount, prefs)
    }
  }

  private spawnGoldLabel(
    x: number,
    y: number,
    amount: number,
    prefs: MotionPrefs
  ): void {
    if (!this.view) return
    const world = worldFromLogical(this.view, { x, y })
    const cell = this.view.cellSize
    const coin = this.add.circle(
      world.x - Math.max(6, cell * 0.22),
      world.y,
      Math.max(3, cell * 0.12),
      THEME.gold,
      0.95
    )
    coin.setStrokeStyle(1, THEME.white, 0.55)
    const label = this.add
      .text(world.x + Math.max(2, cell * 0.06), world.y, formatCoinsFloater(amount), {
        fontSize: `${Math.max(10, Math.floor(cell * 0.32))}px`,
        fontFamily: 'Segoe UI, sans-serif',
        color: '#4ade80',
        fontStyle: 'bold'
      })
      .setOrigin(0, 0.5)
    this.fxLayer.add(coin)
    this.fxLayer.add(label)
    this.fxLive.goldPopups += 1
    this.tweens.add({
      targets: [label, coin],
      y: world.y - 20,
      alpha: 0,
      duration: scaleSimDurationMs({ simMs: 650, prefs }),
      ease: 'Quad.easeOut',
      onComplete: () => {
        label.destroy()
        coin.destroy()
        this.fxLive.goldPopups = Math.max(0, this.fxLive.goldPopups - 1)
      }
    })
  }

  private syncCombatFx(): void {
    if (!this.session || !this.view) return
    const view = this.view
    const prefs = this.motionPrefs()
    const budgetPrefs = {
      simulationSpeed: prefs.simulationSpeed,
      reducedMotion: prefs.reducedMotion
    }
    const dur = (simMs: number, essential = false) =>
      scaleSimDurationMs({ simMs, prefs, essential })

    for (const fx of this.session.consumeCombatFx()) {
      // Shared presentation event → audio (same queue as visuals; no parallel inference).
      if (fx.type === 'towerPulse') {
        const tv = this.towerViews.get(fx.towerId)
        getAudioManager().handleCombatFx(fx, { towerDefinitionId: tv?.definitionId })
      } else {
        getAudioManager().handleCombatFx(fx)
      }
      if (!admitCombatFx(fx, budgetPrefs, this.fxLive)) continue

      if (fx.type === 'splash') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const stroke = fx.color ?? 0x9ca3af
        const fill = fx.color ?? 0x6b7280
        const isShock = fx.innerRadius !== undefined
        const ring = this.add.circle(world.x, world.y, fx.radius * view.cellSize)
        ring.setStrokeStyle(isShock ? 3 : 2, stroke, isShock ? 0.95 : 0.85)
        ring.setFillStyle(fill, isShock ? 0.1 : 0.18)
        this.fxLayer.add(ring)
        if (isShock) {
          // Shockwave — outer zone + inner high-damage ring; octagon shape cue (not glow soup).
          const mid = this.add.circle(world.x, world.y, fx.innerRadius! * view.cellSize)
          mid.setStrokeStyle(2, THEME.selection, 0.7)
          mid.setFillStyle(THEME.selection, 0.06)
          this.fxLayer.add(mid)
          const inner = this.add.circle(
            world.x,
            world.y,
            fx.innerRadius! * view.cellSize * 0.85
          )
          inner.setStrokeStyle(3, THEME.burnMark, 0.95)
          inner.setFillStyle(THEME.burnMark, 0.14)
          this.fxLayer.add(inner)
          if (!prefs.reducedMotion) {
            const oct = this.add.graphics()
            oct.lineStyle(2, THEME.selection, 0.75)
            strokePolygon(oct, regularPolygon(world.x, world.y, fx.radius * view.cellSize * 0.92, 8))
            this.fxLayer.add(oct)
            this.tweens.add({
              targets: oct,
              alpha: 0,
              scale: 1.12,
              duration: dur(260, true),
              ease: 'Quad.easeOut',
              onComplete: () => oct.destroy()
            })
          }
          this.tweens.add({
            targets: [inner, mid],
            alpha: 0,
            scale: 1.18,
            duration: dur(280, true),
            ease: 'Quad.easeOut',
            onComplete: () => {
              inner.destroy()
              mid.destroy()
            }
          })
        } else if (fx.color === 0x38bdf8 && !prefs.reducedMotion) {
          // Frost cold facets — short diamond ticks on the ring.
          for (let i = 0; i < 4; i += 1) {
            const ang = (i / 4) * Math.PI * 2
            const facet = this.add.rectangle(
              world.x + Math.cos(ang) * fx.radius * view.cellSize * 0.7,
              world.y + Math.sin(ang) * fx.radius * view.cellSize * 0.7,
              Math.max(2, view.cellSize * 0.1),
              Math.max(2, view.cellSize * 0.1),
              THEME.frostMark,
              0.8
            )
            facet.setRotation(ang)
            this.fxLayer.add(facet)
            this.tweens.add({
              targets: facet,
              alpha: 0,
              duration: dur(200),
              onComplete: () => facet.destroy()
            })
          }
        }
        this.tweens.add({
          targets: ring,
          alpha: 0,
          scale: 1.15,
          duration: dur(isShock ? 300 : 220, true),
          ease: 'Quad.easeOut',
          onComplete: () => ring.destroy()
        })
        continue
      }

      if (fx.type === 'damageNumber') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const look = damageFloaterLook(fx.style, fx.amount, view.cellSize, prefs.reducedMotion)
        const label = this.add
          .text(world.x, world.y - 8, `${look.prefix}${fx.amount}`, {
            fontSize: `${look.fontSize}px`,
            fontFamily: 'Segoe UI, sans-serif',
            color: look.color,
            fontStyle: 'bold'
          })
          .setOrigin(0.5, 1)
        this.fxLayer.add(label)
        this.fxLive.damageNumbers += 1
        this.tweens.add({
          targets: label,
          y: world.y - look.rise,
          alpha: 0,
          duration: dur(look.simMs),
          ease: 'Quad.easeOut',
          onComplete: () => {
            label.destroy()
            this.fxLive.damageNumbers = Math.max(0, this.fxLive.damageNumbers - 1)
          }
        })
        continue
      }

      if (fx.type === 'goldPopup') {
        this.pendingGold = mergeGoldPopup(this.pendingGold, {
          x: fx.x,
          y: fx.y,
          amount: fx.amount
        })
        this.goldFlushAt = Math.min(
          this.goldFlushAt || this.presentationClock.time + 0.08,
          this.presentationClock.time + 0.08
        )
        continue
      }

      if (fx.type === 'leak') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const label = this.add
          .text(world.x, world.y, `-${fx.amount} ♥`, {
            fontSize: `${Math.max(12, Math.floor(view.cellSize * 0.4))}px`,
            fontFamily: 'Segoe UI, sans-serif',
            color: '#f87171',
            fontStyle: 'bold'
          })
          .setOrigin(0.5)
        this.fxLayer.add(label)
        const ring = this.add.circle(world.x, world.y, Math.max(6, view.cellSize * 0.35), 0, 0)
        ring.setStrokeStyle(2, THEME.danger, 0.9)
        this.fxLayer.add(ring)
        this.tweens.add({
          targets: [label, ring],
          y: world.y - 24,
          alpha: 0,
          scale: 1.25,
          duration: dur(900, true),
          ease: 'Quad.easeOut',
          onComplete: () => {
            label.destroy()
            ring.destroy()
          }
        })
        continue
      }

      if (fx.type === 'hitFlash') {
        const enemyView = this.enemyViews.get(fx.enemyId)
        if (!enemyView) continue
        this.fxLive.hitFlashes += 1
        const release = () => {
          this.fxLive.hitFlashes = Math.max(0, this.fxLive.hitFlashes - 1)
        }
        if (fx.shield) {
          // Hit the shield shell, not the HP body.
          this.tweens.add({
            targets: enemyView.bossRing.visible ? enemyView.bossRing : enemyView.silhouette,
            alpha: prefs.reducedMotion ? 1 : 0.35,
            yoyo: true,
            duration: dur(80),
            onComplete: () => {
              if (enemyView.silhouette.active) enemyView.silhouette.setAlpha(1)
              if (enemyView.bossRing.active) enemyView.bossRing.setAlpha(1)
              release()
            }
          })
          const shell = this.add.circle(
            enemyView.root.x,
            enemyView.root.y,
            Math.max(6, enemyView.lastRadius + 4),
            0,
            0
          )
          shell.setStrokeStyle(2, THEME.shield, 0.95)
          this.fxLayer.add(shell)
          this.tweens.add({
            targets: shell,
            alpha: 0,
            scale: 1.2,
            duration: dur(140),
            onComplete: () => shell.destroy()
          })
        } else {
          this.tweens.add({
            targets: enemyView.silhouette,
            alpha: prefs.reducedMotion ? 1 : 0.55,
            yoyo: true,
            duration: dur(70),
            onComplete: () => {
              if (enemyView.silhouette.active) enemyView.silhouette.setAlpha(1)
              release()
            }
          })
        }
        continue
      }

      if (fx.type === 'shieldBreak') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const shell = this.add.circle(world.x, world.y, Math.max(8, view.cellSize * 0.4), 0, 0)
        shell.setStrokeStyle(3, THEME.shield, 1)
        this.fxLayer.add(shell)
        const shards = prefs.reducedMotion
          ? []
          : [0, 1, 2, 3].map((i) => {
              const shard = this.add.rectangle(
                world.x,
                world.y,
                Math.max(2, view.cellSize * 0.1),
                Math.max(2, view.cellSize * 0.1),
                THEME.shield,
                0.9
              )
              this.fxLayer.add(shard)
              const ang = (i / 4) * Math.PI * 2
              this.tweens.add({
                targets: shard,
                x: world.x + Math.cos(ang) * view.cellSize * 0.5,
                y: world.y + Math.sin(ang) * view.cellSize * 0.5,
                alpha: 0,
                duration: dur(220, true),
                onComplete: () => shard.destroy()
              })
              return shard
            })
        void shards
        this.tweens.add({
          targets: shell,
          scale: 1.4,
          alpha: 0,
          duration: dur(260, true),
          onComplete: () => shell.destroy()
        })
        continue
      }

      if (fx.type === 'regenHeal') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const cue = this.add.circle(
          world.x,
          world.y - view.cellSize * 0.2,
          Math.max(2, view.cellSize * 0.12),
          THEME.regenEnemy,
          0.7
        )
        this.fxLayer.add(cue)
        this.tweens.add({
          targets: cue,
          y: world.y - view.cellSize * 0.55,
          alpha: 0,
          duration: dur(320),
          onComplete: () => cue.destroy()
        })
        continue
      }

      if (fx.type === 'burnApply') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const flame = this.add.triangle(
          world.x,
          world.y - 4,
          0,
          view.cellSize * 0.22,
          -view.cellSize * 0.12,
          -view.cellSize * 0.08,
          view.cellSize * 0.12,
          -view.cellSize * 0.08,
          THEME.burnMark,
          0.9
        )
        this.fxLayer.add(flame)
        this.tweens.add({
          targets: flame,
          y: world.y - view.cellSize * 0.4,
          alpha: 0,
          duration: dur(280, true),
          onComplete: () => flame.destroy()
        })
        continue
      }

      if (fx.type === 'armorBreakApply') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const fracture = fx.fracture === true
        const shard = this.add.rectangle(
          world.x,
          world.y,
          Math.max(3, view.cellSize * (fracture ? 0.22 : 0.18)),
          Math.max(3, view.cellSize * (fracture ? 0.22 : 0.18)),
          THEME.armorBreakMark,
          fracture ? 0.95 : 0.75
        )
        shard.setRotation(Math.PI / 4)
        this.fxLayer.add(shard)
        if (fracture && !prefs.reducedMotion) {
          for (const side of [-1, 1] as const) {
            const chip = this.add.rectangle(
              world.x,
              world.y,
              Math.max(2, view.cellSize * 0.1),
              Math.max(2, view.cellSize * 0.1),
              THEME.armorBreakMark,
              0.85
            )
            chip.setRotation(Math.PI / 4)
            this.fxLayer.add(chip)
            this.tweens.add({
              targets: chip,
              x: world.x + side * view.cellSize * 0.35,
              y: world.y - view.cellSize * 0.2,
              alpha: 0,
              duration: dur(280, true),
              onComplete: () => chip.destroy()
            })
          }
        }
        this.tweens.add({
          targets: shard,
          scale: fracture ? 1.55 : 1.3,
          alpha: 0,
          duration: dur(fracture ? 300 : 220, true),
          onComplete: () => shard.destroy()
        })
        continue
      }

      if (fx.type === 'statusReject') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const ring = this.add.circle(
          world.x,
          world.y,
          Math.max(4, view.cellSize * 0.22),
          THEME.statusReject,
          0
        )
        ring.setStrokeStyle(
          Math.max(2, Math.floor(view.cellSize * 0.05)),
          fx.kind === 'IMMUNE' ? THEME.danger : THEME.statusReject,
          0.95
        )
        this.fxLayer.add(ring)
        const label = this.add
          .text(world.x, world.y - view.cellSize * 0.35, fx.kind === 'IMMUNE' ? 'IMMUNE' : 'RESIST', {
            fontFamily: 'monospace',
            fontSize: `${Math.max(9, Math.floor(view.cellSize * 0.28))}px`,
            color: fx.kind === 'IMMUNE' ? '#f87171' : '#cbd5e1'
          })
          .setOrigin(0.5)
        this.fxLayer.add(label)
        this.tweens.add({
          targets: [ring, label],
          y: `-=${view.cellSize * 0.35}`,
          alpha: 0,
          scale: 1.25,
          duration: dur(420, true),
          onComplete: () => {
            ring.destroy()
            label.destroy()
          }
        })
        continue
      }

      if (fx.type === 'impact') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        this.fxLive.particles += 1
        const color =
          fx.family === 'fire'
            ? THEME.projectileFire
            : fx.family === 'frost'
              ? THEME.projectileFrost
              : fx.family === 'poison'
                ? THEME.projectilePoison
                : fx.family === 'aa'
                  ? THEME.projectileAa
                  : fx.family === 'mage'
                    ? THEME.projectileMage
                    : THEME.projectileCannon
        const burst = this.add.circle(
          world.x,
          world.y,
          Math.max(3, view.cellSize * (fx.signature ? 0.28 : 0.18)),
          color,
          fx.signature ? 0.75 : 0.55
        )
        this.fxLayer.add(burst)
        if (fx.signature && !prefs.reducedMotion) {
          const ring = this.add.circle(world.x, world.y, view.cellSize * 0.3, 0, 0)
          ring.setStrokeStyle(2, THEME.burnMark, 0.95)
          this.fxLayer.add(ring)
          this.tweens.add({
            targets: ring,
            scale: 1.4,
            alpha: 0,
            duration: dur(220, true),
            onComplete: () => ring.destroy()
          })
        }
        this.tweens.add({
          targets: burst,
          scale: fx.signature ? 1.5 : 1.25,
          alpha: 0,
          duration: dur(fx.signature ? 220 : 140),
          onComplete: () => {
            burst.destroy()
            this.fxLive.particles = Math.max(0, this.fxLive.particles - 1)
          }
        })
        continue
      }

      if (fx.type === 'death') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const heavy = fx.heavy === true
        const burst = this.add.circle(
          world.x,
          world.y,
          Math.max(4, view.cellSize * (heavy ? 0.34 : 0.25)),
          fx.color ?? 0xf87171,
          0.7
        )
        this.fxLayer.add(burst)
        if (!prefs.reducedMotion) {
          const count = heavy ? 5 : 3
          for (let i = 0; i < count; i += 1) {
            const shard = this.add.rectangle(
              world.x,
              world.y,
              Math.max(2, view.cellSize * (heavy ? 0.14 : 0.12)),
              Math.max(2, view.cellSize * (heavy ? 0.14 : 0.12)),
              fx.color ?? 0xf87171,
              0.85
            )
            this.fxLayer.add(shard)
            const ang = (i / count) * Math.PI * 2
            this.tweens.add({
              targets: shard,
              x: world.x + Math.cos(ang) * view.cellSize * (heavy ? 0.62 : 0.45),
              y: world.y + Math.sin(ang) * view.cellSize * (heavy ? 0.62 : 0.45),
              alpha: 0,
              duration: dur(enemyDeathSimMs(prefs, heavy)),
              onComplete: () => shard.destroy()
            })
          }
        }
        this.tweens.add({
          targets: burst,
          scale: enemyDeathBurstScale(prefs, heavy),
          alpha: 0,
          duration: dur(enemyDeathSimMs(prefs, heavy)),
          ease: 'Quad.easeOut',
          onComplete: () => burst.destroy()
        })
        continue
      }

      if (fx.type === 'towerPulse') {
        const towerView = this.towerViews.get(fx.towerId)
        if (!towerView) continue
        const scale =
          fx.kind === 'fire'
            ? towerFireKickScale(towerView.definitionId, prefs)
            : fx.kind === 'upgrade'
              ? towerUpgradeKickScale(prefs)
              : towerSellKickScale(prefs)
        const simMs =
          fx.kind === 'fire'
            ? towerFireKickSimMs(towerView.definitionId)
            : fx.kind === 'upgrade'
              ? 160
              : 140
        if (fx.kind === 'fire' && towerView.definitionId === 'cannon' && !prefs.reducedMotion) {
          this.tweens.add({
            targets: towerView.silhouette,
            y: Math.max(1, (this.view?.cellSize ?? 24) * 0.04),
            yoyo: true,
            duration: dur(simMs),
            ease: 'Quad.easeOut',
            onComplete: () => {
              if (towerView.silhouette.active) towerView.silhouette.y = 0
            }
          })
        }
        // FIRE muzzle flare / POISON reactor kick particle.
        if (fx.kind === 'fire' && !prefs.reducedMotion) {
          const flareColor =
            towerView.definitionId === 'frost'
              ? THEME.frost
              : towerView.definitionId === 'poison'
                ? THEME.poison
                : towerView.definitionId === 'cannon'
                  ? THEME.cannon
                  : towerView.definitionId === 'aa'
                    ? THEME.aa
                    : THEME.fire
          const flare = this.add.circle(
            towerView.root.x,
            towerView.root.y,
            Math.max(3, view.cellSize * 0.15),
            flareColor,
            0.65
          )
          this.fxLayer.add(flare)
          this.tweens.add({
            targets: flare,
            scale: 1.8,
            alpha: 0,
            duration: dur(simMs),
            onComplete: () => flare.destroy()
          })
        }
        // AA multi-lock — twin lock diamonds (shape cue, restrained).
        if (fx.kind === 'fire' && fx.multiLock && !prefs.reducedMotion) {
          const lockR = Math.max(6, view.cellSize * 0.42)
          for (const side of [-1, 1] as const) {
            const lock = this.add.rectangle(
              towerView.root.x + side * lockR * 0.55,
              towerView.root.y - lockR * 0.15,
              Math.max(4, view.cellSize * 0.16),
              Math.max(4, view.cellSize * 0.16),
              THEME.aa,
              0.85
            )
            lock.setRotation(Math.PI / 4)
            this.fxLayer.add(lock)
            this.tweens.add({
              targets: lock,
              alpha: 0,
              scale: 1.35,
              duration: dur(220, true),
              onComplete: () => lock.destroy()
            })
          }
        }
        if (fx.kind === 'upgrade') {
          const ring = this.add.circle(
            towerView.root.x,
            towerView.root.y,
            Math.max(8, view.cellSize * 0.55),
            0,
            0
          )
          ring.setStrokeStyle(2, THEME.selection, 0.95)
          this.fxLayer.add(ring)
          this.tweens.add({
            targets: ring,
            scale: 1.35,
            alpha: 0,
            duration: dur(200, true),
            onComplete: () => ring.destroy()
          })
        }
        this.tweens.add({
          targets: towerView.root,
          scaleX: scale,
          scaleY: scale,
          yoyo: true,
          duration: dur(simMs),
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (towerView.root.active) {
              towerView.root.setScale(1)
            }
          }
        })
        continue
      }

      if (fx.type === 'contagion') {
        const from = worldFromLogical(view, { x: fx.fromX, y: fx.fromY })
        const to = worldFromLogical(view, { x: fx.toX, y: fx.toY })
        const line = this.add
          .line(0, 0, from.x, from.y, to.x, to.y, THEME.poisonMark, 0.85)
          .setLineWidth(2)
          .setOrigin(0)
        this.fxLayer.add(line)
        const mote = this.add.circle(from.x, from.y, Math.max(2, view.cellSize * 0.12), THEME.poisonMark, 0.9)
        this.fxLayer.add(mote)
        const pulse = this.add.circle(
          to.x,
          to.y,
          Math.max(3, view.cellSize * 0.2),
          THEME.poisonMark,
          0.35
        )
        this.fxLayer.add(pulse)
        this.tweens.add({
          targets: mote,
          x: to.x,
          y: to.y,
          duration: dur(220, true),
          ease: 'Quad.easeOut'
        })
        this.tweens.add({
          targets: [line, pulse, mote],
          alpha: 0,
          duration: dur(380, true),
          onComplete: () => {
            line.destroy()
            pulse.destroy()
            mote.destroy()
          }
        })
        continue
      }

      if (fx.type === 'deepFreezeFlash') {
        const world = worldFromLogical(view, { x: fx.x, y: fx.y })
        const hex = this.add.graphics()
        hex.fillStyle(THEME.deepFreeze, 0.12)
        hex.lineStyle(2, THEME.deepFreeze, 1)
        const pts = regularPolygon(world.x, world.y, Math.max(10, view.cellSize * 0.42), 6)
        fillPolygon(hex, pts)
        strokePolygon(hex, pts)
        this.fxLayer.add(hex)
        const ring = this.add.circle(
          world.x,
          world.y,
          Math.max(6, view.cellSize * 0.45),
          THEME.deepFreeze,
          0
        )
        ring.setStrokeStyle(3, THEME.deepFreeze, 1)
        this.fxLayer.add(ring)
        this.tweens.add({
          targets: [ring, hex],
          scale: 1.35,
          alpha: 0,
          duration: dur(420, true),
          onComplete: () => {
            ring.destroy()
            hex.destroy()
          }
        })
        continue
      }

      if (fx.type === 'waveClear') {
        // Keep on uiLayer so combat FX cleanup cannot erase the cue.
        const label = this.add
          .text(this.scale.width / 2, this.scale.height * 0.18, `WAVE CLEAR\n+${fx.reward}`, {
            fontSize: '18px',
            fontFamily: 'Segoe UI, sans-serif',
            color: '#86efac',
            align: 'center',
            fontStyle: 'bold'
          })
          .setOrigin(0.5)
          .setScrollFactor(0)
        this.uiLayer.add(label)
        this.tweens.add({
          targets: label,
          alpha: 0,
          y: label.y - 16,
          duration: dur(1400, true),
          ease: 'Quad.easeOut',
          onComplete: () => label.destroy()
        })
      }
    }
  }

  private syncEnemies(): void {
    if (!this.session || !this.view) return
    const view = this.view
    const alive = new Set(this.session.enemies.map((enemy) => enemy.id))
    const barWidth = Math.max(16, Math.floor(view.cellSize * 0.9))
    const markR = Math.max(2, Math.floor(view.cellSize * 0.12))

    for (const [id, enemyView] of this.enemyViews) {
      if (alive.has(id)) continue
      this.tweens.killTweensOf(enemyView.silhouette)
      this.tweens.killTweensOf(enemyView.root)
      enemyView.root.destroy(true)
      this.enemyViews.delete(id)
    }

    for (const enemy of this.session.enemies) {
      const definition =
        this.session.getEnemyDefinition(enemy.definitionId) ?? ENEMY_CATALOG[enemy.definitionId]
      const visual = resolveEnemyVisual(enemy.definitionId)
      const logicalRadius = definition?.radius ?? 0.375
      const radius = Math.max(4, logicalRadius * visual.visualRadiusScale * view.cellSize)
      let enemyView = this.enemyViews.get(enemy.id)
      if (!enemyView) {
        const body = this.add.graphics()
        const shadow = this.add.ellipse(0, 0, radius * 1.4, radius * 0.55, 0x0f172a, 0.35)
        shadow.setVisible(false)
        const eliteRing = this.add.circle(0, 0, radius + 4, 0xffffff, 0)
        // Soft secondary cue only — primary ELITE read is structural paint (orbit pip)
        eliteRing.setStrokeStyle(1.5, 0xfbbf24, 0.35)
        eliteRing.setVisible(false)
        const barBack = this.add.rectangle(0, 0, barWidth, 4, THEME.hpBack)
        const barFill = this.add.rectangle(0, 0, barWidth, 4, THEME.hp).setOrigin(0, 0.5)
        const shieldBack = this.add.rectangle(0, 0, barWidth, 3, THEME.shieldBack)
        const shieldFill = this.add.rectangle(0, 0, barWidth, 3, THEME.shield).setOrigin(0, 0.5)
        const frostMark = this.add.circle(0, 0, markR, THEME.frostMark)
        const poisonMark = this.add.circle(0, 0, markR, THEME.poisonMark)
        const burnMark = this.add.circle(0, 0, markR, THEME.burnMark)
        const armorBreakMark = this.add.circle(0, 0, markR, THEME.armorBreakMark)
        const deepFreezeRing = this.add.circle(0, 0, radius + 3, 0xffffff, 0)
        deepFreezeRing.setStrokeStyle(2, THEME.deepFreeze, 0.95)
        const bossRing = this.add.circle(0, 0, radius + 5, 0xffffff, 0)
        bossRing.setStrokeStyle(2, THEME.selection, 0.55)
        const family = enemyFamilyId(enemy.definitionId)
        bossRing.setVisible(family === 'boss' || family === 'air_boss')
        frostMark.setVisible(false)
        poisonMark.setVisible(false)
        burnMark.setVisible(false)
        armorBreakMark.setVisible(false)
        deepFreezeRing.setVisible(false)
        shieldBack.setVisible(false)
        shieldFill.setVisible(false)
        const debug = this.add
          .text(0, 0, '', {
            fontSize: '9px',
            fontFamily: 'Arial, sans-serif',
            color: '#f8fafc'
          })
          .setOrigin(0.5, 1)
        const silhouette = this.add.container(0, 0, [body, bossRing, eliteRing])
        const root = this.add.container(0, 0, [
          shadow,
          silhouette,
          deepFreezeRing,
          barBack,
          barFill,
          shieldBack,
          shieldFill,
          frostMark,
          poisonMark,
          burnMark,
          armorBreakMark,
          debug
        ])
        this.enemyLayer.add(root)
        const prefs = this.motionPrefs()
        const heavySpawn =
          family === 'boss' || family === 'air_boss' || enemy.enemyRank === 'BOSS'
        silhouette.setScale(enemySpawnStartScale(prefs, heavySpawn))
        enemyView = {
          root,
          silhouette,
          body,
          shadow,
          eliteRing,
          barBack,
          barFill,
          shieldBack,
          shieldFill,
          frostMark,
          poisonMark,
          burnMark,
          armorBreakMark,
          deepFreezeRing,
          bossRing,
          debug,
          definitionId: enemy.definitionId,
          lastRadius: -1,
          lastShield: false,
          lastElite: false,
          lastRegen: false,
          prevX: enemy.position.x,
          prevY: enemy.position.y,
          facing: 0,
          idlePhase: enemyIdlePhase(enemy.id),
          spawnDone: false
        }
        this.enemyViews.set(enemy.id, enemyView)
        this.tweens.add({
          targets: silhouette,
          scaleX: 1,
          scaleY: 1,
          duration: scaleSimDurationMs({ simMs: enemySpawnSimMs(prefs, heavySpawn), prefs }),
          ease: 'Quad.easeOut',
          onComplete: () => {
            enemyView!.spawnDone = true
            if (silhouette.active) silhouette.setScale(1)
          }
        })
      }

      const world = worldFromLogical(view, enemy.position)
      const dx = enemy.position.x - enemyView.prevX
      const dy = enemy.position.y - enemyView.prevY
      const moving = dx * dx + dy * dy > 1e-8
      const face = facingFromDelta(dx, dy)
      if (face != null) {
        enemyView.facing = approachAngle(enemyView.facing, face, 0.35)
      }
      enemyView.prevX = enemy.position.x
      enemyView.prevY = enemy.position.y

      const ratio = enemy.maxHealth <= 0 ? 0 : enemy.health / enemy.maxHealth
      const maxShield = enemy.maxShield ?? 0
      const shield = enemy.shield ?? 0
      const shieldRatio = maxShield <= 0 ? 0 : shield / maxShield
      const hasShield = maxShield > 0 && shield > 0
      const hasFrost = statusRemaining(enemy, 'frost') > 0
      const hasPoison = statusRemaining(enemy, 'poison') > 0
      const hasBurn = statusRemaining(enemy, 'burn') > 0
      const hasArmorBreak = statusRemaining(enemy, 'armorBreak') > 0
      const hasDeepFreeze = statusRemaining(enemy, 'deepFreeze') > 0
      const regenerating =
        Boolean(definition?.regen) &&
        (enemy.regenDelayRemaining ?? 0) <= 0 &&
        enemy.health < enemy.maxHealth
      const isElite = enemy.enemyRank === 'ELITE'

      if (
        enemyView.lastRadius !== radius ||
        enemyView.lastShield !== hasShield ||
        enemyView.lastElite !== isElite ||
        enemyView.lastRegen !== regenerating ||
        enemyView.definitionId !== enemy.definitionId
      ) {
        paintEnemyVisual(enemyView.body, visual, radius, view.cellSize, {
          hasShield,
          regenerating,
          elite: isElite
        })
        enemyView.lastRadius = radius
        enemyView.lastShield = hasShield
        enemyView.lastElite = isElite
        enemyView.lastRegen = regenerating
        enemyView.definitionId = enemy.definitionId
      }

      enemyView.root.setPosition(alignPx(world.x), alignPx(world.y))

      const prefs = this.motionPrefs()
      const air = isAirEnemyFamily(enemy.definitionId)
      const altitude = air ? view.cellSize * 0.18 : 0
      const hover =
        air && !prefs.reducedMotion
          ? Math.sin(this.presentationClock.time * 3.2 + enemyView.idlePhase) * view.cellSize * 0.04
          : 0
      // Presentation-only offset — combat hits still use logical enemy.position via root.
      enemyView.silhouette.setPosition(0, -altitude + hover)
      enemyView.shadow.setVisible(air)
      if (air) {
        enemyView.shadow.setPosition(0, altitude * 0.35)
        enemyView.shadow.setSize(radius * 1.5, radius * 0.5)
        enemyView.shadow.setAlpha(0.32)
      }

      const idleSample = sampleEnemyIdle(
        enemyIdleMotion(enemy.definitionId, prefs),
        this.presentationClock.time,
        enemyView.idlePhase,
        moving
      )
      if (enemyView.spawnDone) {
        enemyView.silhouette.setScale(idleSample.scaleX, idleSample.scaleY)
      }
      // Triangle/fast/flyer/air_boss silhouettes face movement; circles stay upright for HP readability.
      const faceBody =
        visual.silhouette === 'triangle' ||
        visual.silhouette === 'diamond' ||
        visual.silhouette === 'flyer' ||
        visual.silhouette === 'air_boss' ||
        visual.silhouette === 'phase' ||
        enemyFamilyId(enemy.definitionId) === 'fast'
      enemyView.silhouette.setRotation(faceBody ? enemyView.facing + Math.PI / 2 : 0)

      const family = enemyFamilyId(enemy.definitionId)
      const isBoss = family === 'boss' || family === 'air_boss'
      enemyView.bossRing.setVisible(isBoss)
      if (isBoss) {
        enemyView.bossRing.setRadius(radius + 5)
        enemyView.bossRing.setRotation(
          this.presentationClock.time * bossRingSpin(prefs) + enemyView.idlePhase
        )
      }
      // Faint ring only — structural elite accent is in paintEnemyVisual
      enemyView.eliteRing.setVisible(isElite)
      if (isElite) {
        enemyView.eliteRing.setRadius(radius + (isBoss ? 8 : 4))
        enemyView.eliteRing.setPosition(0, -altitude + hover)
      }
      const hpBarH = Math.max(
        3,
        Math.floor(view.cellSize * (isBoss ? 0.2 : visual.silhouette === 'square' ? 0.15 : 0.13))
      )
      const shieldBarH = Math.max(2, Math.floor(view.cellSize * (hasShield ? 0.11 : 0.08)))
      const hpY = -radius - altitude - (isBoss ? 12 : 8)
      // Hide full HP for NORMAL/FAST until damaged; boss/tank/air always show when damaged.
      const showHp =
        isBoss ||
        air ||
        ratio < 0.999 ||
        (visual.silhouette !== 'circle' && visual.silhouette !== 'triangle')
      enemyView.barBack.setVisible(showHp)
      enemyView.barFill.setVisible(showHp)
      const barW = isBoss ? Math.max(barWidth, Math.floor(view.cellSize * 1.2)) : barWidth
      enemyView.barBack.setSize(barW, hpBarH)
      enemyView.barBack.setPosition(0, hpY)
      enemyView.barBack.setStrokeStyle(1, 0x000000, 0.45)
      enemyView.barFill.setSize(Math.max(0, barW * ratio), hpBarH)
      enemyView.barFill.setPosition(-barW / 2, hpY)
      enemyView.shieldBack.setVisible(hasShield)
      enemyView.shieldFill.setVisible(hasShield)
      if (hasShield) {
        const shieldY = hpY - hpBarH - 2
        enemyView.shieldBack.setSize(barW, shieldBarH)
        enemyView.shieldBack.setPosition(0, shieldY)
        enemyView.shieldBack.setStrokeStyle(1, 0x000000, 0.35)
        enemyView.shieldFill.setSize(Math.max(0, barW * shieldRatio), shieldBarH)
        enemyView.shieldFill.setPosition(-barW / 2, shieldY)
      }

      // Ordered status arcs under the enemy (boss: further out so they don't cover the body).
      const markY = radius + markR + (isBoss ? 6 : 2)
      const marks = [
        { on: hasFrost, node: enemyView.frostMark },
        { on: hasPoison, node: enemyView.poisonMark },
        { on: hasBurn, node: enemyView.burnMark },
        { on: hasArmorBreak, node: enemyView.armorBreakMark }
      ]
      const active = marks.filter((m) => m.on)
      marks.forEach((m) => m.node.setVisible(false))
      active.forEach((m, i) => {
        const offset = (i - (active.length - 1) / 2) * markR * 2.4
        m.node.setVisible(true)
        m.node.setRadius(markR)
        m.node.setPosition(offset, markY)
      })

      enemyView.deepFreezeRing.setRadius(radius + 3)
      enemyView.deepFreezeRing.setPosition(0, 0)
      enemyView.deepFreezeRing.setVisible(hasDeepFreeze)
      enemyView.debug.setPosition(0, -radius - 14)
      if (this.showPath) {
        const base = definition?.speed ?? 0
        const slow = effectiveSlow(enemy)
        const speed = effectiveSpeed(base, enemy)
        enemyView.debug.setText(
          `${enemy.id} hp${enemy.health.toFixed(0)}` +
            (maxShield > 0 ? ` sh${shield.toFixed(0)}` : '') +
            ` s${speed.toFixed(2)}` +
            ` F${statusRemaining(enemy, 'frost').toFixed(1)}` +
            ` P${statusRemaining(enemy, 'poison').toFixed(1)}` +
            ` B${statusRemaining(enemy, 'burn').toFixed(1)}` +
            ` DF${statusRemaining(enemy, 'deepFreeze').toFixed(1)}` +
            ` slow${(slow * 100).toFixed(0)}%`
        )
      } else {
        enemyView.debug.setText('')
      }
      enemyView.debug.setVisible(this.showPath)
    }
  }

  private clearEnemyViews(): void {
    for (const enemyView of this.enemyViews.values()) {
      this.tweens.killTweensOf(enemyView.silhouette)
      this.tweens.killTweensOf(enemyView.root)
      enemyView.root.destroy(true)
    }
    this.enemyViews.clear()
  }

  private bindDebugKeys(): void {
    const keyboard = this.input.keyboard
    if (!keyboard) return

    keyboard.on('keydown-G', () => {
      this.showCoordinates = !this.showCoordinates
      this.coordLayer.setVisible(this.showCoordinates)
    })
    keyboard.on('keydown-P', () => {
      this.showPath = !this.showPath
      this.pathLayer.setVisible(this.showPath)
    })
    keyboard.on('keydown-ESC', () => this.handleEscape())
    keyboard.on('keydown-SPACE', () => this.togglePause())
  }

  private handleEscape(): void {
    if (this.isPlacing()) {
      this.cancelPlacement()
      return
    }
    if (useGameStore.getState().selectedTowerId) {
      useGameStore.getState().selectTowerId(null)
      return
    }
    this.togglePause()
  }

  private cancelPlacement(): void {
    this.touchConfirmAnchor = null
    this.previewAnchor = null
    this.clearPreview()
    useGameStore.getState().cancelPlacement()
  }

  private togglePause(): void {
    if (!this.session) return
    if (this.session.state !== 'PLAYING' && this.session.state !== 'READY') return
    if (this.session.paused) return
    this.cancelPlacement()
    this.session.setPaused(true)
    useGameStore.getState().publishSessionView(this.session.view())
  }

  /** Dev/manual inspection of the render camera. */
  getCameraDebug(): {
    cellSize: number
    scrollX: number
    scrollY: number
    maxScrollX: number
    maxScrollY: number
    needsPan: boolean
    cameraMode: string
    mapPixelWidth: number
    mapPixelHeight: number
    viewportWidth: number
    viewportHeight: number
  } | null {
    if (!this.view) return null
    return {
      cellSize: this.view.cellSize,
      scrollX: this.view.scrollX,
      scrollY: this.view.scrollY,
      maxScrollX: this.view.maxScrollX,
      maxScrollY: this.view.maxScrollY,
      needsPan: this.view.maxScrollX > 0 || this.view.maxScrollY > 0,
      cameraMode: this.view.cameraMode,
      mapPixelWidth: this.view.mapPixelWidth,
      mapPixelHeight: this.view.mapPixelHeight,
      viewportWidth: this.view.viewportWidth,
      viewportHeight: this.view.viewportHeight
    }
  }

  /** Dev helper to pan without gestures. */
  debugPanBy(dx: number, dy: number): void {
    this.panBy(dx, dy)
  }
}
